import React, { memo } from "react";
import { validators, formats } from "investira.sdk";
import { Stack, Grid, useTheme } from "investira.react.components";

const CorrelacaoCenario = memo((props) => {
  const { variacoes } = props;

  const theme = useTheme();

  const xNeutral = theme.palette.background.paper;
  const xNeg = theme.palette.error.light;
  const xPos = theme.palette.primary.main;

  const xNeutralRgb = parseColorToRgb(xNeutral);
  const xNegRgb = parseColorToRgb(xNeg);
  const xPosRgb = parseColorToRgb(xPos);

  function parseColorToRgb(pInput) {
    if (!pInput) return { r: 0, g: 0, b: 0 };
    if (pInput.startsWith("#")) {
      let xC = pInput.slice(1);
      if (xC.length === 3)
        xC = xC
          .split("")
          .map((xCh) => xCh + xCh)
          .join("");
      const xNum = parseInt(xC, 16);
      return { r: (xNum >> 16) & 255, g: (xNum >> 8) & 255, b: xNum & 255 };
    }
    const xM = pInput.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (xM) return { r: +xM[1], g: +xM[2], b: +xM[3] };
    return { r: 0, g: 0, b: 0 };
  }

  function rgbToCss({ r, g, b }) {
    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
  }

  function mixRgb(pA, pB, pT) {
    return {
      r: pA.r + (pB.r - pA.r) * pT,
      g: pA.g + (pB.g - pA.g) * pT,
      b: pA.b + (pB.b - pA.b) * pT,
    };
  }

  const getCellColor = (pValue, pRange = 100) => {
    if (pValue === null || pValue === undefined) return rgbToCss(xNeutralRgb);
    // Normaliza o valor para a escala de 0 a 1, dividindo pelo range máximo
    // Se pRange for 0 ou não fornecido, usa 100 como padrão
    const xMaxRange = pRange > 0 ? pRange : 100;
    const xNormalizedValue = Math.min(1, Math.abs(pValue) / xMaxRange);
    if (pValue > 0)
      return rgbToCss(mixRgb(xNeutralRgb, xPosRgb, xNormalizedValue));
    return rgbToCss(mixRgb(xNeutralRgb, xNegRgb, xNormalizedValue));
  };

  const xAtivoCorrelacoes = Object.values(variacoes);

  // Calcula o maior valor absoluto já multiplicado por 100 (mesmo formato usado na função getCellColor)
  const xMaiorValorAbsoluto =
    xAtivoCorrelacoes && xAtivoCorrelacoes.length
      ? Math.max(
          ...xAtivoCorrelacoes.map((xItem) =>
            Math.abs((xItem.fator - 1 ?? 0) * 100)
          )
        )
      : 100; // Se não houver valores, usa 100 como padrão

  return (
    <>
      <Grid
        container
        spacing={2}
        columns={{ xs: 4, sm: 8, md: 12, lg: 16, xl: 18 }}
      >
        {(() => {
          if (!xAtivoCorrelacoes) return null;
          // Ordena as correlações por fator, sem mutar o original
          const xCorrelacoesSorted = xAtivoCorrelacoes
            .slice()
            .sort((xA, xB) => {
              if (xA.fator - 1 > xB.fator - 1) return -1;
              if (xA.fator - 1 < xB.fator - 1) return 1;
              return 0;
            });
          // Renderiza cada item como um bloco agrupado (indicador + valor)
          return xCorrelacoesSorted.map((xCorrelacoes, xCorIndex) => {
            const xCorrelacaoValue =
              xCorrelacoes.fator === null || xCorrelacoes.fator === undefined
                ? "---"
                : xCorrelacoes.fator === 1
                ? "1"
                : xCorrelacoes.fator === -1
                ? "-1"
                : formats.formatNumber((xCorrelacoes.fator - 1) * 100, 4);

            const xBenchmark = xCorrelacoes.indicador_id;

            return (
              <Grid key={xCorrelacoes.id ?? xCorIndex} md={3} lg={2} xl={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    borderRadius: 1,
                    overflow: "hidden",
                    boxShadow: 1,
                    bgcolor: "background.paper",
                    minWidth: "fit-content",
                    my: 0.25,
                    mr: 0.5,
                  }}
                >
                  <Stack
                    px={1}
                    py={0.5}
                    sx={{
                      minWidth: "50%",
                      backgroundColor: "transparent",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {xBenchmark}
                  </Stack>
                  <Stack
                    px={1}
                    py={0.5}
                    sx={{
                      minWidth: "50%",
                      backgroundColor: getCellColor(
                        (xCorrelacoes.fator - 1) * 100,
                        xMaiorValorAbsoluto
                      ),
                      borderRadius: 0.5,
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      color: validators.isNull(xCorrelacoes.fator)
                        ? (theme) => theme.palette.text.secondary
                        : (theme) =>
                            theme.palette.getContrastText(
                              getCellColor(
                                (xCorrelacoes.fator - 1) * 100,
                                xMaiorValorAbsoluto
                              )
                            ),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      fontWeight: 600,
                    }}
                  >
                    {xCorrelacaoValue}
                  </Stack>
                </Stack>
              </Grid>
            );
          });
        })()}
      </Grid>
    </>
  );
});

export default CorrelacaoCenario;
